import keyBy from 'lodash/keyBy';

import { useTranslation } from '@lyearn/i18n';
import { Stack, Typography } from '@lyearn/ui';
import ImageWithHotspots from '@/components/ImageWithHotspots';

import ReviewIcon from '../../components/QuestionReview/ReviewIcon';
import AnswerMap from '../../fill-in-the-blanks/QuestionReview/AnswerMap';
import { OptionsListItemTypeEnum } from '../../single-select/types';
import { QuestionReviewProps } from '../../types';
import { getImageHotspotResponseType } from '../utils';
import CorrectAnswers from './CorrectAnswers';

const QuestionReview: React.FC<QuestionReviewProps> = ({
  question,
  correctAnswers,
  questionAttempt,
}) => {
  const { t } = useTranslation(['QuizPlayer', 'Lygo']);

  const imageHotspotComponents = question.imageHotspotComponents;
  const imageHotspotResponse = questionAttempt?.submission?.imageHotspotResponse || [];
  const correctImageHotspotAnswers = correctAnswers?.correctImageHotspotAnswers;
  const isSubmitted = Boolean(questionAttempt?.isSubmitted);
  const imageHotspotResponseById = keyBy(imageHotspotResponse, 'id');
  const correctAnswerCount = imageHotspotComponents.filter(
    (imageHotspotComponent) =>
      getImageHotspotResponseType({
        imageHotspotResponse,
        imageHotspotComponent,
        correctImageHotspotAnswers,
      }) === OptionsListItemTypeEnum.Correct,
  ).length;

  const showCorrectAnswers =
    isSubmitted &&
    correctImageHotspotAnswers &&
    imageHotspotComponents.length !== correctAnswerCount;

  const hotspots = question.imageHotspotComponents.map((hotspotComponent) => ({
    id: hotspotComponent.id,
    x: hotspotComponent.x,
    y: hotspotComponent.y,
  }));
  const media = question.attachments![0];

  return (
    <>
      <ImageWithHotspots noCaption readOnly hotspots={hotspots} media={media} />
      {imageHotspotResponse.length ? (
        imageHotspotComponents.map((imageHotspotComponent, index) => {
          const type = getImageHotspotResponseType({
            imageHotspotResponse,
            imageHotspotComponent,
            correctImageHotspotAnswers,
          });
          const response = imageHotspotResponseById[imageHotspotComponent.id];

          return (
            <Stack key={imageHotspotComponent.id} className="px-8" gapX={12}>
              {isSubmitted ? <ReviewIcon type={type} /> : null}
              <AnswerMap labelLeft={`${index + 1}`} labelRight={response?.text} />
              {isSubmitted && type === OptionsListItemTypeEnum.Missed ? (
                <Typography color="text-text-success" variant="body-short-02">
                  {t('QuizPlayer:Missed')}
                </Typography>
              ) : null}
            </Stack>
          );
        })
      ) : (
        <Typography className="ms-8" color="text-text-placeholder" variant="label-02">
          {t('QuizPlayer:NotAnswered')}
        </Typography>
      )}
      {showCorrectAnswers ? (
        <CorrectAnswers
          correctImageHotspotAnswers={correctImageHotspotAnswers}
          imageHotspotComponents={imageHotspotComponents}
        />
      ) : null}
    </>
  );
};

export default QuestionReview;
